/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, react/prop-types */

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Inline, NodeData, Node } from '@contentful/rich-text-types';
import { useTheme, useMediaQuery, Box, Grid, TableContainer, Table as MuiTable, TableBody, TableRow, TableCell, Typography, TypographyProps, SxProps, TableHead } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Children, Fragment, ReactNode } from 'react';
import { Card, ContentTypeRichTextContent, Entry, Table as TableProps } from '@/types/generated';
import ContentTypeRichText from '../ContentTypeRichText';
import TemplateContainer from '../TemplateContainer';
import { compareVariantTCGridStyles, compareVariantTHCellGrid, compareVariantTHCellGridBox, compareVariantTHCellGridNestedBox, compareVariantTHCellGridParentBox, compareVariantTRBoxStyles, containerWrapperStyles, defaultVariantTableCellStyles, defaultVariantTableHeaderCellStyles, defaultVariantTableRowStyles, defaultVariantTableStyles, getHeadingStyles, titleAndDescriptionCtrStyles } from './Table.styles';
import { CheckedIcon, XMarkIcon, getCompareTableHeader, hasPlaceHolders, placeHolderValues, removePlaceHolderValues } from './Table.utils';
import TableFromJson, { ITableFromJson } from './TableFromJson';
import FocalPointImage from '@shared-components/components/FocalPointImage/FocalPointImage';
import { ContentfulBackground, HeadingTag, Maybe, Sizes, TableVariant } from '@shared-components/types';
import { textToJumpLink } from '@utils';
import { resolveContentfulBgColor } from '@utils/resolveContentfulBgColor';
import getHeadingVariant from '@utils/resolveContentfulHeadingVariant';
export interface ITable extends TableProps {
  /**
   * Optional styles to apply to the wrapper container.
   *
   * @type {SxProps}
   * @memberof IContentTypeRichText
   */
  containerSx?: SxProps;
  titleHtag?: Maybe<HeadingTag>;
  /**
   * Optional json data to render the table.
   *
   * @type {SxProps}
   * @memberof IContentTypeRichText
   */
  tableJsonData?: ITableFromJson;
  /**
   * Optional json data to render the table for mobile if different from desktop.
   *
   * @type {SxProps}
   * @memberof IContentTypeRichText
   */
  tableJsonDataMobile?: ITableFromJson;
  /**
   * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
   *
   * @type {Maybe<string>}
   * @memberof ITable
   */
  anchorId?: Maybe<string>;
  backgroundColor?: Maybe<string>;
}
interface ITableNodes {
  hasBorder: Maybe<boolean>;
  textAlign?: 'center' | 'left';
  isSmallHeader?: Maybe<boolean>;
  hasHeaderBackgroundColor?: Maybe<boolean>;
  cellSpacing?: Maybe<Omit<Sizes, 'XLarge'>>;
}
interface IDefaultTableNodes extends ITableNodes {
  mobileWidth: boolean;
  theme: Theme;
  fontSize: TypographyProps['variant'];
  isDarkBackground: boolean;
}
const getCellSpacing = (size: Sizes): string => {
  const sizes = {
    Small: '0.5',
    Medium: '1',
    Large: '1.5'
  } as {
    [key: string]: string;
  };
  return `${sizes[size || 'Medium']}rem`;
};
const rowHasHeader = (row: NodeData) => {
  return row.content.some((cell: NodeData) => cell.nodeType === BLOCKS.TABLE_HEADER_CELL);
};
const renderDefaultTableMark = (hasCheckedPlaceholder: boolean, hasXMarkPlaceholder: boolean) => {
  if (hasCheckedPlaceholder) {
    return <CheckedIcon />;
  }
  if (hasXMarkPlaceholder) {
    return <XMarkIcon />;
  }
  return null;
};
interface ICompareTableOptions {
  mobileWidth: boolean;
  theme: Theme;
  tableContent: ITable['tableContent'];
}
const getCompareMarkValue = (node: NodeData): string | null => {
  const isPlaceHolderXMark = node?.content[0]?.content[0]?.value === placeHolderValues.XMARK || node?.content[1]?.content[0]?.value === placeHolderValues.XMARK;
  const isPlaceHolderChecked = node?.content[0]?.content[0]?.value === placeHolderValues.CHECKED || node?.content[1]?.content[0]?.value === placeHolderValues.CHECKED;
  if (isPlaceHolderXMark) {
    return placeHolderValues.XMARK;
  }
  if (isPlaceHolderChecked) {
    return placeHolderValues.CHECKED;
  }
  return null;
};
const getCompareTableOptions = ({
  mobileWidth,
  theme,
  tableContent
}: Readonly<ICompareTableOptions>) => {
  return {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_: NodeData, children: ReactNode) => {
        const p = _?.content[0]?.value || '';
        const para = p.includes('row*title');
        return <p style={{
          display: para ? 'none' : 'block',
          margin: '16px 0 0 0'
        }}>
						<Typography variant="bodyLargeBook" color="text.secondary">
							{children as string}
						</Typography>
					</p>;
      },
      [BLOCKS.TABLE]: (_: NodeData, children: ReactNode) => {
        return <MuiTable>{children}</MuiTable>;
      },
      [BLOCKS.TABLE_ROW]: (_: NodeData, children: ReactNode) => {
        const rowTitleSource = _.content[0]?.content[0]?.content[0]?.value ?? '';
        let rowTitle = '';
        let titleRegular = '';
        let titleBold = '';
        if (rowTitleSource.includes('row*title')) {
          const splitTitle = rowTitleSource.split('*');
          rowTitle = splitTitle[splitTitle.length - 1];
          const indexOfOpeningP = rowTitle.indexOf('(');
          const indexOfClosingP = rowTitle.indexOf(')');
          titleRegular = rowTitle.substring(indexOfOpeningP, indexOfClosingP + 1);
          titleBold = rowTitle.replace(titleRegular, '');
        }
        return <Grid container spacing={2} wrap="nowrap" justifyContent="space-between" position="relative">
						{rowTitle && <Box sx={compareVariantTRBoxStyles}>
								<Typography variant="bodyLargeBold" color="text.primary" component="h2">
									{titleBold}
								</Typography>

								{titleRegular && <Typography variant="bodyLargeBook" color="text.primary">
										{titleRegular}
									</Typography>}
							</Box>}
						{children}
					</Grid>;
      },
      [BLOCKS.TABLE_CELL]: (_: NodeData, children: ReactNode) => {
        const embeddedEntries = _.content[0].content.filter((el: Inline) => el.nodeType === 'embedded-entry-inline');
        if (embeddedEntries.length > 0) {
          return <Grid item sm={4} md={4} sx={{
            ...compareVariantTCGridStyles(mobileWidth)
          }}>
							{embeddedEntries.map((elem: Node) => {
              const targetId = (elem as NodeData).data?.target?.sys?.id as string | undefined;
              const targetTitle = (elem as NodeData).data?.target?.title as string | undefined;
              return <Fragment key={targetId ?? targetTitle ?? 'embedded-entry-inline'}>
										<Typography variant="bodySmallBook" color="text.primary">
											{(elem as NodeData).data.target.title as string}
										</Typography>
									</Fragment>;
            })}
						</Grid>;
        }
        const mark = getCompareMarkValue(_);
        let renderedCellContent = children;
        if (mark === placeHolderValues.CHECKED) {
          renderedCellContent = <CheckedIcon />;
        } else if (mark === placeHolderValues.XMARK) {
          renderedCellContent = <XMarkIcon />;
        }
        return <Grid item sm={4} md={4} sx={{
          ...compareVariantTCGridStyles(mobileWidth)
        }}>
						{renderedCellContent}
					</Grid>;
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node: NodeData) => {
        const title = getCompareTableHeader(node.content[0].content);
        const target = node?.content[0];
        const inlineEntryContent = target?.content?.filter((content: Node) => content.nodeType === 'embedded-entry-inline');
        const targetSysId = inlineEntryContent?.[0] ? (inlineEntryContent[0] as NodeData).data?.target?.sys?.id : undefined;
        const inlineEntries = tableContent?.links?.entries.inline;
        const matchingInlineEntry = inlineEntries?.find((entry: Maybe<Entry>) => entry?.sys.id === targetSysId);
        return <Grid item sm={4} md={4} sx={compareVariantTHCellGrid}>
						<Box sx={{
            ...compareVariantTHCellGridBox(theme)
          }}>
							<Typography variant="bodySmallBook" color="text.primary">
								{title}
							</Typography>

							{matchingInlineEntry && <Box sx={compareVariantTHCellGridParentBox}>
									<Box sx={{
                ...compareVariantTHCellGridNestedBox(theme)
              }} data-testid={`table_header_image_box_${matchingInlineEntry?.sys.id}`}>
										{(matchingInlineEntry as Card)?.image && <FocalPointImage image={(matchingInlineEntry as Card)?.image} />}
									</Box>
								</Box>}
						</Box>
					</Grid>;
      }
    }
  };
};
const getTableNodes = ({
  hasBorder = true,
  textAlign = 'center',
  isSmallHeader = false,
  hasHeaderBackgroundColor = true,
  cellSpacing = 'Medium',
  mobileWidth,
  theme,
  fontSize,
  isDarkBackground
}: Readonly<IDefaultTableNodes>) => {
  return {
    renderNode: {
      [BLOCKS.TABLE]: (node: NodeData, children: ReactNode) => {
        const childRows = Children.toArray(children);
        const headerRows: ReactNode[] = [];
        const bodyRows: ReactNode[] = [];
        node.content.forEach((row: NodeData, index: number) => {
          (rowHasHeader(row) ? headerRows : bodyRows).push(childRows[index]);
        });
        return <MuiTable sx={{
          ...defaultVariantTableStyles(hasBorder, mobileWidth)
        }}>
						{headerRows.length > 0 && <TableHead>{headerRows}</TableHead>}
						<TableBody>{bodyRows}</TableBody>
					</MuiTable>;
      },
      [BLOCKS.TABLE_ROW]: (_: unknown, children: ReactNode) => {
        const sxProp = {
          ...defaultVariantTableRowStyles(hasBorder)
        };
        return <TableRow sx={sxProp}>{children}</TableRow>;
      },
      [BLOCKS.TABLE_CELL]: (_: NodeData) => {
        const placeholders = hasPlaceHolders(_.content[0]?.content);
        const fieldValue = removePlaceHolderValues(_.content[0]?.content[0].value);
        const textColor = isDarkBackground ? 'text.light' : 'text.primary';
        const mark = renderDefaultTableMark(Boolean(placeholders[placeHolderValues.CHECKED]), Boolean(placeholders[placeHolderValues.XMARK]));
        return <TableCell align={textAlign} sx={{
          ...defaultVariantTableCellStyles(placeholders, hasBorder, theme),
          padding: getCellSpacing(cellSpacing as Sizes)
        }}>
						<Typography variant={fontSize} color={textColor}>
							{fieldValue}
						</Typography>
						{mark}
					</TableCell>;
      },
      [BLOCKS.TABLE_HEADER_CELL]: (_: NodeData) => {
        const placeholders = hasPlaceHolders(_.content[0]?.content);
        const fieldValue = removePlaceHolderValues(_.content[0]?.content[0].value);
        const textColor = isDarkBackground || placeholders[placeHolderValues.HIGHLIGHTED] ? 'text.light' : 'text.primary';
        return <TableCell component="th" scope="col" align={textAlign} sx={{
          ...defaultVariantTableHeaderCellStyles(placeholders, hasBorder, theme, hasHeaderBackgroundColor !== false),
          padding: getCellSpacing(cellSpacing as Sizes)
        }}>
						<Typography variant={isSmallHeader ? 'bodyLargeBold' : 'header4'} color={textColor} sx={{
            margin: isSmallHeader ? '0' : '1.38rem 0'
          }}>
							{fieldValue}
						</Typography>
					</TableCell>;
      }
    }
  };
};
export default function Table({
  border = true,
  fontSize = 'bodySmallBook',
  footnote,
  tableContent,
  variant = 'default',
  firstColumnSticky,
  containerSx = {},
  tableJsonData,
  tableJsonDataMobile,
  title,
  titleHtag = 'h1',
  descriptionRichText,
  anchorId,
  backgroundColor
}: Readonly<ITable>) {
  const theme = useTheme();
  const mobileWidth = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const jsonToRenderTable = isSmallScreen && tableJsonDataMobile ? tableJsonDataMobile : tableJsonData;
  const renderTableFromJson = Boolean(jsonToRenderTable?.rows?.length);
  const isDarkBackground = variant === 'simple' && Boolean(backgroundColor?.toLowerCase().includes('dark'));
  const resolvedFontSize = (fontSize ?? 'bodySmallBook') as TypographyProps['variant'];
  const getTableVariant = (variant: TableVariant) => {
    switch (variant) {
      case 'compare':
        return compareTableOptions;
      case 'simple':
        return simpleTableOptions;
      default:
        return defaultTableOptions;
    }
  };
  const defaultTableOptions = getTableNodes({
    hasBorder: border !== false,
    mobileWidth,
    theme,
    fontSize: resolvedFontSize,
    isDarkBackground
  });
  const simpleTableOptions = getTableNodes({
    hasBorder: false,
    textAlign: 'left',
    isSmallHeader: true,
    hasHeaderBackgroundColor: false,
    cellSpacing: 'Small',
    mobileWidth,
    theme,
    fontSize: resolvedFontSize,
    isDarkBackground
  });
  const compareTableOptions = getCompareTableOptions({
    mobileWidth,
    theme,
    tableContent
  });
  const bgColor = variant === 'simple' ? resolveContentfulBgColor(backgroundColor as ContentfulBackground, theme) : '';
  return <TemplateContainer dataTestId="section_table_container" containerSx={{
    ...containerWrapperStyles(containerSx, renderTableFromJson, bgColor)
  }} maxWidth={renderTableFromJson ? false : 'lg'} {...anchorId && {
    id: textToJumpLink(anchorId)
  }} data-sentry-element="TemplateContainer" data-sentry-component="Table" data-sentry-source-file="Table.tsx">
			{(title || descriptionRichText) && <Box sx={titleAndDescriptionCtrStyles}>
					{title && <Typography variant={getHeadingVariant(titleHtag)} component={titleHtag === 'normal' ? 'p' : titleHtag ?? 'h1'} sx={{
        ...getHeadingStyles(theme)
      }} whiteSpace="pre-line" data-testid="section_table_title">
							{title}
						</Typography>}
					{descriptionRichText && <ContentTypeRichText content={descriptionRichText as ContentTypeRichTextContent} dataTestId={`section_table_description_rich_text`} backgroundColor="transparent" disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" docProps={{
        variant: isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook',
        textAlign: 'center'
      }} />}
				</Box>}

			{renderTableFromJson ? <TableFromJson jsonData={jsonToRenderTable} firstColumnSticky={firstColumnSticky} /> : <TableContainer>
					{documentToReactComponents(tableContent?.json, getTableVariant(variant as TableVariant))}
				</TableContainer>}
			{footnote?.json && <ContentTypeRichText dataTestId="section_table_footnote" content={footnote} disableGutters disablePaddingOn={['Top', 'Bottom']} />}
		</TemplateContainer>;
}